import React, { useState, useEffect, useRef } from 'react';
import { List, ListItem, ListItemIcon, ListItemText, Typography, Box, useMediaQuery } from '@mui/material';
import {  useLocation, useNavigate } from 'react-router-dom';
import { decodeToken } from '../../utils/settings';
import { useFullMenuStructure, MODULE_TO_SECTION } from './menuconfig'
import { ArrowBack } from '@mui/icons-material';
import { resetEntityState } from '../Common/Entity/actions/reduxActions';
import { getSelectedEntityStatus } from '../Reducers/Entity/EntityReducer';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { STATUS } from '../Common/Entity/Enums';

const Sidebar = ({ 
  moduleKey = null, 
  showFullMenu = false,
  onMenuItemClick = null,
  mobile = false,
  firmOnboarded = true,
  manufacturerSelected = null,
  onFirmModalShow = null,
  onManufacturerModalShow = null,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 1200px)');
  const [selectedKey, setSelectedKey] = useState(location.pathname);
  const [tokenData, setTokenData] = useState(null);
  const token = localStorage.getItem('token');
  const sidebarRef = useRef(null);
  // Close sidebar on outside click
  useEffect(() => {
    if (!onMenuItemClick) return;
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onMenuItemClick();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onMenuItemClick]);

  useEffect(() => {
    setSelectedKey(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const getTokenData = async () => {
      if (token) {
        try {
          const decoded = await decodeToken(token);
          setTokenData(decoded);
        } catch (err) {
          console.error('Failed to decode token', err);
        }
      }
    };
    getTokenData();
  }, [token]);

  const dashboardConfig = useSelector((state) => state.dashboardReducer.dashboardConfig);

  const allowedKeys = dashboardConfig?.menu_config?.map((item) => item.key) ?? [];

  const isAllowed = (key) => {
    if (!Array.isArray(allowedKeys) || allowedKeys.length === 0) return true;
    return allowedKeys.includes(key);
  };
  const menuLabelMap = React.useMemo(() => {
    const map = {};
    if (dashboardConfig?.menu_config) {
      dashboardConfig.menu_config.forEach(item => {
        map[item.key] = item.label;
      });
    }
    return map;
  }, [dashboardConfig]);


  const entityStatus = useSelector(getSelectedEntityStatus);
  const { selectedEntityInfo, selectedSupplierData } = useSelector(
    (state) => state.entityReducer
  );
  const hasAccess = selectedEntityInfo?.has_access;
  const handleMenuItemClick = (item) => {
    if (selectedSupplierData.nudge) {
      toast.warning( selectedSupplierData?.nudge);
      return;
    }
    if (entityStatus === STATUS.INACTIVE && selectedEntityInfo?.has_history === false) {
      if (onManufacturerModalShow) {
        onManufacturerModalShow();
      }
      return;
    }
    dispatch({ type: 'RESET_SELECTED_FIRM' });
    if (!firmOnboarded && onFirmModalShow) {
      onFirmModalShow();
      return;
    }
    if (firmOnboarded && !manufacturerSelected && onManufacturerModalShow) {
      onManufacturerModalShow();
      return;
    }

    navigate(item.path);
    setSelectedKey(item.path);
    
    if (onMenuItemClick) {
      onMenuItemClick();
    }
  };
  const { menuStructure: fullMenuStructure } = useFullMenuStructure();
  const menuStructure = React.useMemo(() => {
    if (showFullMenu) {
      return fullMenuStructure;
    }
    if (!moduleKey) {
      return fullMenuStructure;
    }
     const filteredSections = fullMenuStructure.filter(section => {
        const matchesSection = section.section === MODULE_TO_SECTION[moduleKey];
        const hasMatchingItems = section.items.some(item => 
          item.moduleKey === moduleKey || 
          item.section === moduleKey
        );
        return matchesSection || hasMatchingItems;
      }).map(section => ({
        ...section,
        items: section.items.filter(item => {
          const matches = item.moduleKey === moduleKey || item.section === moduleKey;
          return matches;
        })
      }));
      return filteredSections;
  }, [fullMenuStructure, moduleKey, showFullMenu, tokenData]);

  return (
    <Box sx={{ width: '100%', position: 'relative', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }} ref={sidebarRef}>
      <Box
        sx={{
          width: 300,
          height: { xs: '100vh', lg: 'calc(100vh - 150px)' },
          position: 'absolute',
          top: -1,
          left: 0,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
          backgroundBlendMode: 'overlay',
          zIndex: 0,
          opacity: 0.6,
          border: '.5px solid rgba(255,255,255, 0.2)',
        }}
      />
      <Box
        sx={{
          width: 300,
          height: { xs: '100vh', lg: 'calc(100vh - 150px)' },
          position: 'absolute',
          top: 0,
          left: 0,
          borderRadius: 2,
          backgroundColor: !mobile ? 'rgba(0, 0, 0, 0)' : '#ffffffde',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          boxShadow: `
            inset 1px 1px 4px 0px rgba(255, 255, 255, 0.6),
            inset 1px 1px 0px 0px rgba(255, 255, 255, 1),
            inset 10px 0px 20px 0px rgba(0, 0, 0, 0.05),
            inset 1px 4px 40px 1px rgba(255, 255, 255, 1),
            inset 0px 2px 16px 1px rgba(255, 255, 255, 1),
            inset 0px -2px 0px 0px rgba(255, 255, 255, 1),
            inset -1px -4px 16px 0px rgba(255, 255, 255, 0.8)
          `,
          zIndex: 1,
          
        }}
      />
      <List
        sx={{
          width: 300,
          height: { xs: '100vh',  lg:'calc(100vh - 150px)', xl: 'calc(100vh - 150px)' },
          overflowY: 'auto',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 2,
          padding: { md: 1.5 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {menuStructure.map((menu) => {
          const visibleItems = menu.items.filter((item) => isAllowed(item.key));
          if (visibleItems.length === 0) return null;

          return (
            <li key={menu.section} style={{ marginBottom: '32px' }}>
              <ul style={{ padding: 0, margin: 0 }}>
                {!isMobile && (
                  <Typography
                    variant="overline"
                    sx={{ fontSize: '11px', pl: 2, py: 1, color: '#666' }}
                  >
                    {menu.section}
                  </Typography>
                )}
                {isMobile && (
                  <Box sx={{ display: 'flex', alignItems:'center', px: 2, py:1, mb: 2, borderBottom: '1px solid #ddd'}}>
                    <ArrowBack onClick={() => {
                        if (onMenuItemClick) {
                          onMenuItemClick();
                        }
                      }} 
                    />
                    <Typography
                      variant="h2"
                      sx={{ fontSize: '11px', pl: 2, py: 1, color: '#666' }}
                    >
                      {menu.section}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mx: 2 }}>
                  {visibleItems.map((item) => (
                    <ListItem
                      button
                      key={item.key}
                      selected={selectedKey === item.path}
                      onClick={() => handleMenuItemClick(item)}
                      sx={{
                        px: 2,
                        borderRadius: 2,
                        mb: 0.5,
                        py: 0.5,
                        color: '#333',
                        '&:hover': {
                          backgroundColor: '#995A000D',
                        },
                        '&.Mui-selected': {
                          py: 0.5,
                          backgroundColor: '#995A001F',
                          boxShadow: `
                            inset -1px -1px 1px 0px #8C8C8C33,
                            inset 1px 1px 6px 0px #FFFFFFB5,
                            inset -1px -1px 1px 0px #FFFFFF7A,
                            inset 1px 1px 1px 0px #FFFFFF99
                          `,
                          color: '#995A00 !important',
                          '& .MuiListItemIcon-root': {
                            color: '#995A00',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={menuLabelMap[item.key] || item.title }
                        primaryTypographyProps={{
                          fontWeight: selectedKey === item.path ? 600 : 400,
                          color: selectedKey === item.path ? '#995A00' : '#333',
                        }}
                      />
                    </ListItem>
                  ))}
                </Box>
              </ul>
            </li>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
